import { Component, ElementRef, afterNextRender, viewChild } from '@angular/core';

const BF_MAP_CENTER: google.maps.LatLngLiteral = { lat: 47.506051, lng: 19.079212 };
const BF_MAP_ZOOM = 16;

// Restrict this key to this site's domain(s) via HTTP referrers in the
// Google Cloud Console -- https://console.cloud.google.com/google/maps-apis.
const GOOGLE_MAPS_API_KEY = 'AIzaSyBBbUhWRYwePSGQV680v7jtQ0Jd2GNVCok';

// Dark/sepia theme, matching the reference site's map block.
const BF_MAP_STYLE: google.maps.MapTypeStyle[] = [
  {
    featureType: 'all',
    elementType: 'labels.text.fill',
    stylers: [{ saturation: 36 }, { color: '#8e8065' }, { lightness: 40 }],
  },
  {
    featureType: 'all',
    elementType: 'labels.text.stroke',
    stylers: [{ visibility: 'on' }, { color: '#000000' }, { lightness: 16 }],
  },
  {
    featureType: 'all',
    elementType: 'labels.icon',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'administrative',
    elementType: 'geometry.fill',
    stylers: [{ color: '#443b32' }],
  },
  {
    featureType: 'administrative',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#000000' }, { lightness: 17 }, { weight: 1.2 }],
  },
  {
    featureType: 'landscape',
    elementType: 'all',
    stylers: [{ visibility: 'on' }],
  },
  {
    featureType: 'landscape',
    elementType: 'geometry',
    stylers: [{ color: '#565048' }, { lightness: -22 }],
  },
  {
    featureType: 'landscape',
    elementType: 'geometry.fill',
    stylers: [{ lightness: 45 }, { color: '#2e2925' }, { saturation: 0 }],
  },
  {
    featureType: 'landscape',
    elementType: 'labels.icon',
    stylers: [{ saturation: -100 }, { lightness: -54 }],
  },
  {
    featureType: 'poi',
    elementType: 'all',
    stylers: [{ visibility: 'on' }, { lightness: 0 }],
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [{ color: '#2e2925' }, { lightness: 5 }],
  },
  {
    featureType: 'poi',
    elementType: 'labels.icon',
    stylers: [{ saturation: -89 }, { lightness: -55 }],
  },
  {
    featureType: 'road',
    elementType: 'labels.icon',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.fill',
    stylers: [{ color: '#8f7a5b' }, { lightness: 0 }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#8f7a5b' }, { lightness: 0 }, { weight: 0.2 }],
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry',
    stylers: [{ color: '#8f7a5b' }, { lightness: 0 }],
  },
  {
    featureType: 'road.local',
    elementType: 'geometry',
    stylers: [{ color: '#565048' }, { lightness: 0 }],
  },
  {
    featureType: 'transit',
    elementType: 'geometry',
    stylers: [{ color: '#443b32' }, { lightness: 12 }],
  },
  {
    featureType: 'transit.station',
    elementType: 'labels.icon',
    stylers: [{ visibility: 'on' }, { saturation: -100 }, { lightness: -51 }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#443b32' }, { lightness: 15 }],
  },
];

let googleMapsLoader: Promise<void> | null = null;

function loadGoogleMaps(): Promise<void> {
  if (typeof google !== 'undefined' && google.maps) {
    return Promise.resolve();
  }

  googleMapsLoader ??= new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load the Google Maps script.'));
    document.head.appendChild(script);
  });

  return googleMapsLoader;
}

@Component({
  selector: 'app-map',
  imports: [],
  templateUrl: './map.html',
  styleUrl: './map.scss',
})
export class MapSection {
  private readonly mapEl = viewChild.required<ElementRef<HTMLDivElement>>('mapEl');

  constructor() {
    // Google Maps touches `window`/`document`, so it can only load once
    // the component is actually mounted in a browser (not during SSR).
    afterNextRender(() => {
      loadGoogleMaps()
        .then(() => this.renderMap())
        .catch((error: unknown) => console.error(error));
    });
  }

  private renderMap(): void {
    const map = new google.maps.Map(this.mapEl().nativeElement, {
      center: BF_MAP_CENTER,
      zoom: BF_MAP_ZOOM,
      styles: BF_MAP_STYLE,
      disableDefaultUI: false,
      scrollwheel: true,
    });

    // Teardrop pin. Built here (not at module scope) because
    // `google.maps.Point` doesn't exist until the Maps script has
    // actually loaded.
    const icon: google.maps.Symbol = {
      path: 'M12 0C7.31 0 3.5 3.81 3.5 8.5c0 6.5 8.5 15.5 8.5 15.5s8.5-9 8.5-15.5C20.5 3.81 16.69 0 12 0z',
      fillColor: '#ffc107',
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
      scale: 1.5,
      anchor: new google.maps.Point(12, 24),
    };

    new google.maps.Marker({
      position: BF_MAP_CENTER,
      map,
      icon,
    });
  }
}
